/**
 * Audio Ref Context
 *
 * HTMLAudioElement ref만 관리하는 최소한의 Context
 * 오디오 재생 상태는 playerStore에서 관리합니다.
 */

'use client';

import React, { createContext, useContext, useRef, useCallback, useEffect } from 'react';
import { usePlayerStore } from '@/lib/stores/playerStore';

// ============================================================================
// Types
// ============================================================================

interface AudioRefContextValue {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  seekTo: (time: number) => void;
  skipForward: (seconds?: number) => void;
  skipBackward: (seconds?: number) => void;
}

// ============================================================================
// Context
// ============================================================================

const AudioRefContext = createContext<AudioRefContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

export function AudioRefProvider({ children }: { children: React.ReactNode }) {
  const audioRef = useRef<HTMLAudioElement>(null);

  // Store에서 필요한 상태만 구독 (액션은 getState()로 가져옴)
  const isPlaying = usePlayerStore((state) => state.isPlaying);
  const currentTrack = usePlayerStore((state) => state.currentTrack);
  const volume = usePlayerStore((state) => state.volume);
  const isMuted = usePlayerStore((state) => state.isMuted);
  const duration = usePlayerStore((state) => state.duration);
  // repeatMode는 handleEnded에서 getState()로 가져옴 (stale closure 방지)

  // 볼륨 변경 시 audio element에 적용
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  // 재생 상태 동기화
  useEffect(() => {
    if (!audioRef.current || !audioRef.current.src) {
      return;
    }

    if (isPlaying) {
      audioRef.current.play().catch(() => {
        // 재생 실패 시 pause 상태로 변경 (getState 사용으로 무한 루프 방지)
        usePlayerStore.getState().pause();
      });
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying]);

  // 트랙 변경 시 오디오 소스 업데이트
  useEffect(() => {
    const audioUrl = currentTrack?.version?.audioUrl;

    if (audioRef.current && audioUrl) {
      const audio = audioRef.current;
      const shouldAutoPlay = usePlayerStore.getState().isPlaying;

      // canplay 이벤트 핸들러
      const handleCanPlay = () => {
        if (shouldAutoPlay) {
          audio.play().catch(() => {
            usePlayerStore.getState().pause();
          });
        }
        audio.removeEventListener('canplay', handleCanPlay);
      };

      // 소스 설정 및 로드
      audio.src = audioUrl;
      audio.addEventListener('canplay', handleCanPlay);
      audio.load();

      return () => {
        audio.removeEventListener('canplay', handleCanPlay);
      };
    }
  }, [currentTrack?.id, currentTrack?.version?.audioUrl]);

  // ============================================================================
  // Actions
  // ============================================================================

  const seekTo = useCallback((time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      usePlayerStore.getState().setCurrentTime(time);
    }
  }, []);

  const skipForward = useCallback(
    (seconds = 10) => {
      if (audioRef.current) {
        const newTime = Math.min(audioRef.current.currentTime + seconds, duration);
        audioRef.current.currentTime = newTime;
        usePlayerStore.getState().setCurrentTime(newTime);
      }
    },
    [duration]
  );

  const skipBackward = useCallback((seconds = 10) => {
    if (audioRef.current) {
      const newTime = Math.max(audioRef.current.currentTime - seconds, 0);
      audioRef.current.currentTime = newTime;
      usePlayerStore.getState().setCurrentTime(newTime);
    }
  }, []);

  // ============================================================================
  // Audio Event Handlers
  // ============================================================================

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      usePlayerStore.getState().setCurrentTime(audioRef.current.currentTime);
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      usePlayerStore.getState().setDuration(audioRef.current.duration);
    }
  }, []);

  const handleEnded = useCallback(() => {
    const currentRepeatMode = usePlayerStore.getState().repeatMode;
    if (currentRepeatMode === 'one') {
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play();
      }
    } else {
      usePlayerStore.getState().nextTrack();
    }
  }, []);

  const value: AudioRefContextValue = {
    audioRef,
    seekTo,
    skipForward,
    skipBackward,
  };

  return (
    <AudioRefContext.Provider value={value}>
      {children}
      {/* 숨겨진 오디오 엘리먼트 */}
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        preload="metadata"
      />
    </AudioRefContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * useAudioRef 훅
 *
 * 오디오 요소에 대한 직접 제어가 필요할 때 사용합니다.
 * (seekTo, skipForward, skipBackward)
 */
export function useAudioRef() {
  const context = useContext(AudioRefContext);
  if (!context) {
    throw new Error('useAudioRef must be used within AudioRefProvider');
  }
  return context;
}

/**
 * useAudioRefOptional 훅
 *
 * Provider 외부에서도 안전하게 사용 가능
 */
export function useAudioRefOptional() {
  return useContext(AudioRefContext);
}
